"use client";

import { useEffect, useState } from "react";
import styles from "./chain-card.module.scss";
import Image from "next/image";
import { useBridge } from "@/features/bridge/providers/bridge.provider";
import classNames from "classnames";
import { motion, useAnimationControls } from "framer-motion";
import { BLOCKCHAINS_BY_NETWORK, ChainData } from "@/constants/chain-details";
import ModalPopper from "@components/layout/modal-popper/modal-popper";

type Props = {
  chainOrigin?: boolean; // If it is the "from" chain
};

const ChainCard = ({ chainOrigin = false }: Props) => {
  const [chainList, setChainList] = useState<ChainData[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const {
    bridgeControls: {
      fromChain: { originChain, setOriginChain },
      toChain: { destinationChain, setDestinationChain },
    },
  } = useBridge();

  const controls = useAnimationControls();

  const chainData = chainOrigin ? originChain : destinationChain;
  const handleSelectChain = (chain: ChainData) => {
    if (chainOrigin) setOriginChain(chain);
    else setDestinationChain(chain);

    setIsOpen(false);
  };

  useEffect(() => {
    // Controls the animation on blockchain change
    controls.set({
      opacity: 0,
      x: -10,
    });
    controls.start({
      opacity: 1,
      x: 0,
    });
  }, [chainData, controls]);

  useEffect(() => {
    // TODO Is this even needed anymore?
    setChainList(BLOCKCHAINS_BY_NETWORK);
    setOriginChain(undefined);
    setDestinationChain(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setDestinationChain, setOriginChain]);

  return (
    <div className={styles.cardContainer}>
      <button className={styles.chainSelectBtn} onClick={() => setIsOpen(true)}>
        {chainData ? (
          <motion.div
            className={classNames(styles.chainOverview)}
            initial={{ x: -10 }}
            transition={{ bounce: 0, duration: 0.3 }}
            animate={controls}
          >
            <Image src={chainData.logo} alt="chain-icon" />
            <div className={styles.chainDetails}>
              <span className={styles.chainPosition}>
                {chainOrigin ? "From" : "To"}
              </span>
              <span className={styles.chainTicker}>{chainData.ticker}</span>
              <span className={styles.chainName}>{chainData.chainName}</span>
            </div>
          </motion.div>
        ) : (
          <p className={styles.pickChain}>Select chain</p>
        )}
      </button>
      <ModalPopper isOpen={isOpen} onClose={() => setIsOpen(false)}>
        <div className={styles.container}>
          <p className={styles.chainListHeader}>Select chain</p>
          <ul className={styles.chainList}>
            {chainList.map((chain) => {
              // if (chainData && chain.chainId === chainData.chainId) return;
              return (
                <li
                  key={chain.chainName}
                  role="button"
                  onClick={() => handleSelectChain(chain)}
                  className={classNames(
                    chainData?.chainId === chain.chainId && styles.active,
                  )}
                >
                  <Image
                    src={chain.logo}
                    alt="chain-logo"
                    className={styles.chainLogo}
                  />
                  <div className={styles.chainDetails}>
                    <p>{chain.chainName}</p>
                    <span>{chain.ticker}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </ModalPopper>
    </div>
  );
};

export default ChainCard;
